import React, { useState, useEffect, useCallback } from 'react';
import './IoTDashboard.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.gboundy.ca';

const CAMERA_IDS = {
  'rumsey camera': 'rumsey-camera',
  'cottage camera': 'cottage-camera'
};

function CameraCard({ device }) {
  const [snapshotUrl, setSnapshotUrl] = useState(null);
  const [snapshotTime, setSnapshotTime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cameraId = CAMERA_IDS[device.label.toLowerCase()] || 
    Object.entries(CAMERA_IDS).find(([k]) => device.label.toLowerCase().includes(k.split(' ')[0]))?.[1];

  const formatTimestamp = (date) => {
    if (!date) return '';
    return date.toLocaleString('en-CA', {
      timeZone: 'America/Toronto',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    }) + ' ET';
  };

  const fetchSnapshot = useCallback(async () => {
    if (!cameraId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/iot/camera-snapshot/${cameraId}`);
      if (!response.ok) throw new Error('Failed to fetch snapshot');
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
      setSnapshotUrl(url);
      const serverTime = response.headers.get('X-Snapshot-Time');
      setSnapshotTime(serverTime ? new Date(serverTime) : new Date());
    } catch (err) {
      setError('Snapshot unavailable');
      console.error('Snapshot error:', err);
    } finally {
      setLoading(false);
    }
  }, [cameraId, snapshotUrl]);

  useEffect(() => {
    fetchSnapshot();
    return () => {
      if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
    };
  }, [cameraId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="device-card camera">
      <h3>{device.label}</h3>
      <div className="snapshot-controls">
        <button onClick={fetchSnapshot} disabled={loading} className="refresh-btn">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
        {snapshotTime && <span className="snapshot-time">{formatTimestamp(snapshotTime)}</span>}
      </div>
      {error ? (
        <div className="camera-error">
          <div>{error}</div>
          <small>If camera was factory reset, update RTSP URL in cameras.json</small>
        </div>
      ) : snapshotUrl ? (
        <img src={snapshotUrl} alt={device.label} className="camera-snapshot" />
      ) : (
        <div className="camera-placeholder">{loading ? 'Loading snapshot...' : 'No snapshot available'}</div>
      )}
    </div>
  );
}

function IoTDashboard({ location = 'thepoint' }) {
  const title = location === 'rumsey' ? 'Rumsey Controls' : 'ThePoint Controls';
  const [devices, setDevices] = useState([]);
  const [deviceStatuses, setDeviceStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authToken, setAuthToken] = useState(() => sessionStorage.getItem('iot_token'));
  const [pin, setPin] = useState('');
  const [authError, setAuthError] = useState('');
  const [pendingDevices, setPendingDevices] = useState([]);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/api/iot/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin })
      });
      const data = await response.json();
      if (data.success) {
        sessionStorage.setItem('iot_token', data.token);
        setAuthToken(data.token);
        setAuthError('');
      } else {
        setAuthError(data.error || 'Invalid PIN');
      }
    } catch (err) {
      setAuthError('Connection error');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('iot_token');
    setAuthToken(null);
    setPin('');
  };

  const fetchDevices = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iot/devices?location=${location}`);
      const data = await response.json();
      setDevices(data.devices || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch devices');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [location]);

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/iot/pending-device-counts`);
      const counts = await response.json();
      setPendingCount(counts[location] || 0);
    } catch (err) {
      console.error('Failed to fetch pending counts:', err);
    }
  }, [location]);

  const fetchPendingDevices = useCallback(async () => {
    if (!authToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/iot/pending-devices/${location}`, {
        headers: { 'X-IoT-Token': authToken }
      });
      const data = await response.json();
      setPendingDevices(data);
    } catch (err) {
      console.error('Failed to fetch pending devices:', err);
    }
  }, [location, authToken]);

  const handleApproveDevice = async (deviceId, deviceType) => {
    if (!authToken) return;
    try {
      await fetch(`${API_BASE}/api/iot/pending-devices/${deviceId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-IoT-Token': authToken },
        body: JSON.stringify({ device_type: deviceType })
      });
      fetchPendingDevices();
      fetchPendingCount();
    } catch (err) {
      console.error('Failed to approve device:', err);
    }
  };

  const handleIgnoreDevice = async (deviceId) => {
    if (!authToken) return;
    try {
      await fetch(`${API_BASE}/api/iot/pending-devices/${deviceId}/ignore`, {
        method: 'POST',
        headers: { 'X-IoT-Token': authToken }
      });
      fetchPendingDevices();
      fetchPendingCount();
    } catch (err) {
      console.error('Failed to ignore device:', err);
    }
  };

  const runClassifier = async () => {
    if (!authToken) return;
    try {
      const response = await fetch(`${API_BASE}/api/iot/run-classifier`, {
        method: 'POST',
        headers: { 'X-IoT-Token': authToken }
      });
      const data = await response.json();
      if (data.success) {
        fetchPendingDevices();
        fetchPendingCount();
      }
    } catch (err) {
      console.error('Failed to run classifier:', err);
    }
  };

  const fetchDeviceStatus = useCallback(async (deviceId) => {
    try {
      const response = await fetch(`${API_BASE}/api/iot/device/${deviceId}/status`);
      const data = await response.json();
      setDeviceStatuses(prev => ({ ...prev, [deviceId]: data }));
    } catch (err) {
      console.error(`Failed to fetch status for ${deviceId}:`, err);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
    fetchPendingCount();
  }, [fetchDevices, fetchPendingCount]);

  useEffect(() => {
    if (devices.length > 0) {
      devices.forEach(d => fetchDeviceStatus(d.id));
      const interval = setInterval(() => {
        devices.forEach(d => fetchDeviceStatus(d.id));
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [devices, fetchDeviceStatus]);

  const toggleSwitch = async (deviceId, currentState) => {
    if (!authToken) return;
    const newState = currentState === 'on' ? 'off' : 'on';
    try {
      await fetch(`${API_BASE}/api/iot/device/${deviceId}/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-IoT-Token': authToken },
        body: JSON.stringify({ state: newState })
      });
      setDeviceStatuses(prev => ({
        ...prev,
        [deviceId]: { ...prev[deviceId], switch: newState }
      }));
    } catch (err) {
      console.error('Failed to toggle switch:', err);
    }
  };

  const setThermostat = async (deviceId, setpoint) => {
    if (!authToken) return;
    try {
      await fetch(`${API_BASE}/api/iot/device/${deviceId}/thermostat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-IoT-Token': authToken },
        body: JSON.stringify({ setpoint })
      });
      setDeviceStatuses(prev => ({
        ...prev,
        [deviceId]: { ...prev[deviceId], heatingSetpoint: setpoint }
      }));
      setTimeout(() => fetchDeviceStatus(deviceId), 3000);
    } catch (err) {
      console.error('Failed to set thermostat:', err);
    }
  };

  const switches = devices.filter(d => d.type === 'switch');
  const thermostats = devices.filter(d => d.type === 'thermostat');
  const cameras = devices.filter(d => d.type === 'camera');

  const HEAT_SWITCHES = ['Kitchen', 'Living', 'Queen', 'Dining', 'Brick', 'Blue'];
  const WATER_SWITCHES = ['Water Pump', 'UV filter', 'Hot Water Heater'];
  
  const isWinterSeason = () => {
    const now = new Date();
    const month = now.getMonth() + 1;
    const day = now.getDate();
    return (month >= 11) || (month <= 3 && (month < 3 || day <= 21));
  };

  const getGroupSwitches = (group) => {
    const patterns = group === 'heat' ? HEAT_SWITCHES : WATER_SWITCHES;
    let matched = switches.filter(s => patterns.some(p => s.label.toLowerCase().includes(p.toLowerCase())));
    if (group === 'water' && isWinterSeason()) {
      const wasteLine = switches.find(s => s.label.toLowerCase().includes('waste'));
      if (wasteLine && !matched.includes(wasteLine)) matched.push(wasteLine);
    }
    return matched;
  };

  const getGroupState = (group) => {
    const groupSwitches = getGroupSwitches(group);
    if (groupSwitches.length === 0) return 'unknown';
    const states = groupSwitches.map(s => deviceStatuses[s.id]?.switch);
    if (states.every(s => s === 'on')) return 'on';
    if (states.every(s => s === 'off')) return 'off';
    return 'mixed';
  };

  const toggleGroup = async (group) => {
    if (!authToken) return;
    const groupSwitches = getGroupSwitches(group);
    const currentState = getGroupState(group);
    const newState = currentState === 'on' ? 'off' : 'on';
    
    for (const device of groupSwitches) {
      try {
        await fetch(`${API_BASE}/api/iot/device/${device.id}/switch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-IoT-Token': authToken },
          body: JSON.stringify({ state: newState })
        });
        setDeviceStatuses(prev => ({
          ...prev,
          [device.id]: { ...prev[device.id], switch: newState }
        }));
      } catch (err) {
        console.error(`Failed to toggle ${device.label}:`, err);
      }
    }
  };

  if (!authToken) {
    return (
      <div className="iot-dashboard">
        <div className="login-container">
          <h1>{title}</h1>
          <form onSubmit={handleLogin} className="login-form">
            <input
              type="password"
              placeholder="Enter Code"
              value={pin}
              onChange={(e) => setPin(e.target.value.toUpperCase())}
              maxLength={6}
              autoFocus
              autoComplete="off"
              autoCapitalize="characters"
            />
            <button type="submit">Login</button>
          </form>
          {authError && <div className="auth-error">{authError}</div>}
        </div>
      </div>
    );
  }

  if (loading) return <div className="iot-loading">Loading devices...</div>;
  if (error) return <div className="iot-error">{error}</div>;

  return (
    <div className="iot-dashboard">
      <div className="iot-header">
        <h1>{title}</h1>
        <div className="header-right">
          <button 
            className={`pending-alert-btn ${pendingCount === 0 ? 'inactive' : ''}`}
            onClick={() => { if (pendingCount > 0) { setShowPendingModal(true); fetchPendingDevices(); }}}
            disabled={pendingCount === 0}
          >
            {pendingCount} New Device{pendingCount !== 1 ? 's' : ''}
          </button>
          <div className="credits-box">
            Constructed by: factory.ai factory-droid[bot]<br />
            as directed by G. Boundy<br />
            Build: {process.env.REACT_APP_BUILD_TIME || 'dev'}
          </div>
          <button onClick={handleLogout} className="logout-btn">Logout</button>
        </div>
      </div>

      {showPendingModal && (
        <div className="modal-overlay" onClick={() => setShowPendingModal(false)}>
          <div className="pending-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Devices at {location === 'rumsey' ? 'Rumsey' : 'ThePoint'}</h2>
              <button className="modal-close" onClick={() => setShowPendingModal(false)}>×</button>
            </div>
            <div className="modal-actions">
              <button onClick={runClassifier} className="classify-btn">Run Auto-Classifier</button>
            </div>
            <div className="pending-list">
              {pendingDevices.length === 0 ? (
                <p>No pending devices</p>
              ) : (
                pendingDevices.map(device => (
                  <div key={device.id} className="pending-device">
                    <div className="pending-device-info">
                      <strong>{device.hostname || device.ip}</strong>
                      <span className="pending-mac">{device.mac}</span>
                      <span className="pending-vendor">{device.best_vendor || device.vendor || 'Unknown vendor'}</span>
                      {device.device_guess && <span className="pending-guess">Type: {device.device_guess}</span>}
                      {device.open_ports && <span className="pending-ports">Ports: {JSON.stringify(device.open_ports)}</span>}
                    </div>
                    <div className="pending-device-actions">
                      <button onClick={() => handleApproveDevice(device.id, device.device_guess)} className="approve-btn">Approve</button>
                      <button onClick={() => handleIgnoreDevice(device.id)} className="ignore-btn">Ignore</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {location === 'thepoint' && (
        <div className="group-controls">
          <button 
            className={`group-btn heat ${getGroupState('heat')}`}
            onClick={() => toggleGroup('heat')}
          >
            HEAT {getGroupState('heat') === 'on' ? '(ON)' : getGroupState('heat') === 'off' ? '(OFF)' : '(MIXED)'}
          </button>
          <button 
            className={`group-btn water ${getGroupState('water')}`}
            onClick={() => toggleGroup('water')}
          >
            WATER {getGroupState('water') === 'on' ? '(ON)' : getGroupState('water') === 'off' ? '(OFF)' : '(MIXED)'}
            {isWinterSeason() && ' +Waste'}
          </button>
        </div>
      )}
      
      <section className="device-section">
        <h2>Switches ({switches.length})</h2>
        <div className="device-grid">
          {switches.map(device => {
            const status = deviceStatuses[device.id] || {};
            const isOn = status.switch === 'on';
            return (
              <div key={device.id} className={`device-card switch ${isOn ? 'on' : 'off'}`}>
                <h3>{device.label}</h3>
                <div className="switch-status">{status.switch || '...'}</div>
                <button 
                  className={`toggle-btn ${isOn ? 'on' : 'off'}`}
                  onClick={() => toggleSwitch(device.id, status.switch)}
                >
                  {isOn ? 'Turn Off' : 'Turn On'}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <section className="device-section">
        <h2>Thermostats ({thermostats.length})</h2>
        <div className="device-grid">
          {thermostats.map(device => {
            const status = deviceStatuses[device.id] || {};
            return (
              <div key={device.id} className="device-card thermostat">
                <h3>{device.label}</h3>
                {status.connectivity && (
                  <div className={`connectivity ${status.connectivity.toLowerCase()}`}>
                    {status.connectivity}
                  </div>
                )}
                <div className="thermostat-info">
                  <div className="temp-display">
                    <span className="current-temp">
                      {status.temperature !== undefined ? `${status.temperature}°` : '...'}
                    </span>
                    <span className="temp-label">Current</span>
                  </div>
                  <div className="setpoint-display">
                    <span className="setpoint-temp">
                      {status.heatingSetpoint !== undefined ? `${status.heatingSetpoint}°` : '...'}
                    </span>
                    <span className="temp-label">Setpoint</span>
                  </div>
                  {status.humidity !== undefined && (
                    <div className="humidity-display">
                      <span className="humidity-value">{status.humidity}%</span>
                      <span className="temp-label">Humidity</span>
                    </div>
                  )}
                </div>
                <div className="thermostat-controls">
                  <button onClick={() => setThermostat(device.id, (status.heatingSetpoint || 20) - 1)}>-</button>
                  <span className="mode">{status.mode || '...'}</span>
                  <button onClick={() => setThermostat(device.id, (status.heatingSetpoint || 20) + 1)}>+</button>
                </div>
                <div className={`operating-state ${status.healthState === "OFFLINE" ? "offline" : status.operatingState}`}>{status.healthState === "OFFLINE" ? "OFFLINE" : (status.operatingState || "")}</div>
                {status.ecoMode && status.ecoMode !== 'off' && (
                  <div className="eco-mode">ECO: {status.ecoHeat}° - {status.ecoCool}°</div>
                )}
                {device.source === 'nest' && status.availableModes && (
                  <div className="available-modes">
                    Modes: {status.availableModes.map(m => m.toLowerCase()).join(', ')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {cameras.length > 0 && (
        <section className="device-section">
          <h2>Cameras ({cameras.length})</h2>
          <div className="device-grid">
            {cameras.map(device => (
              <CameraCard key={device.id} device={device} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export default IoTDashboard;
